"use client";

import { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  fadeUp,
  stagger,
  cardItem,
  fastStagger,
  viewportOnce,
} from "../components/motionVariants";
import {
  ShieldCheck,
  BarChart2,
  Target,
  RefreshCw,
  Package,
  Factory,
} from "lucide-react";

// ─── Articles ─────────────────────────────────────────────────────────
const ARTICLES = [
  {
    id: 1,
    slug: "e-invoice-irn-automatic-b2b",
    title:
      "How to Generate E-Invoice (IRN) Automatically for Your B2B Transactions",
    excerpt:
      "E-Invoice is mandatory for eligible taxpayers. Here's exactly how ABSS Books generates the Invoice Reference Number (IRN) automatically - and what you need to set up first.",
    category: "GST & Compliance",
    tag: "E-Invoice",
    date: "12 May 2026",
    readTime: "6 min read",
    image: "/images/blog-e-invoice.jpg",
    featured: true,
  },
  {
    id: 2,
    slug: "desktop-vs-cloud-accounting-2026",
    title:
      "Desktop vs Cloud Accounting: A Practical Comparison for Indian SMEs in 2026",
    excerpt:
      "Desktop accounting software has served Indian businesses for decades. But cloud accounting offers things a desktop install simply can't - here's an honest side-by-side.",
    category: "Accounting & Finance",
    tag: "Comparison",
    date: "8 May 2026",
    readTime: "8 min read",
    image: "/images/blog-desktop-vs-cloud.jpg",
    featured: true,
  },
  {
    id: 4,
    slug: "qr-code-lead-capture-trade-shows",
    title:
      "QR Code Lead Capture at Trade Shows: How to Never Lose a Lead Again",
    excerpt:
      "Your sales team meets 200 people at an expo. 150 of those leads get lost in notes, WhatsApp messages, and sticky papers. Here's how ABSS CRM's QR capture fixes this permanently.",
    category: "CRM & Sales",
    tag: "Lead Management",
    date: "28 Apr 2026",
    readTime: "5 min read",
    image: "/images/blog-qr-lead-capture.jpg",
    featured: false,
  },
  {
    id: 5,
    slug: "signs-outgrown-excel-need-crm",
    title: "5 Signs Your Business Has Outgrown Excel and Needs a Real CRM",
    excerpt:
      "Most Indian SMEs start with spreadsheets. Here are the five inflection points where Excel stops working - and what you should move to instead.",
    category: "CRM & Sales",
    tag: "Growth",
    date: "22 Apr 2026",
    readTime: "4 min read",
    image: "/images/blog-excel-to-crm.jpg",
    featured: false,
  },
  {
    id: 6,
    slug: "e-way-bill-for-manufacturers",
    title:
      "E-Way Bill for Manufacturers: What Triggers It, How to Generate It, and Common Mistakes",
    excerpt:
      "E-Way Bill is required for shipments above ₹50,000. Manufacturers frequently make mistakes on Part B. This guide covers the full workflow in ABSS Books.",
    category: "GST & Compliance",
    tag: "E-Way Bill",
    date: "15 Apr 2026",
    readTime: "7 min read",
    image: "/images/blog-eway-bill.jpg",
    featured: false,
  },
  {
    id: 7,
    slug: "multi-branch-accounting",
    title:
      "Multi-Branch Accounting: Managing 3 Locations Without Hiring 3 Accountants",
    excerpt:
      "Running multiple branches doesn't have to mean multiple accounts, multiple spreadsheets, and multiple CAs. Here's how ABSS Books centralises everything.",
    category: "Accounting & Finance",
    tag: "Multi-Branch",
    date: "10 Apr 2026",
    readTime: "6 min read",
    image: "/images/blog-multi-branch.jpg",
    featured: false,
  },
  {
    id: 8,
    slug: "lead-to-invoice-crm-books-integration",
    title:
      "From Lead to Invoice Without Re-Entering Data: How ABSS Bridges Sales and Accounts",
    excerpt:
      "The handoff between sales and accounts is where data gets lost, re-entered, and corrupted. Here's how the CRM-to-Books integration in ABSS eliminates this entirely.",
    category: "Product",
    tag: "Integration",
    date: "5 Apr 2026",
    readTime: "5 min read",
    image: "/images/blog-crm-books-sync.jpg",
    featured: false,
  },
  {
    id: 9,
    slug: "debtors-aging-report-collections",
    title:
      "Debtors Aging Report Explained: How to Use It to Speed Up Collections",
    excerpt:
      "Your debtors aging report tells you exactly who owes you money and for how long. Here's how to read it, act on it, and reduce overdue receivables using ABSS Books + CRM.",
    category: "Accounting & Finance",
    tag: "Reports",
    date: "28 Mar 2026",
    readTime: "5 min read",
    image: "/images/blog-debtors-aging.jpg",
    featured: false,
  },
];

const CATEGORIES = [
  "All",
  "GST & Compliance",
  "CRM & Sales",
  "Accounting & Finance",
  "Product",
];

const FEATURED_TOPICS = [
  { Icon: ShieldCheck, label: "GST & E-Invoice" },
  { Icon: BarChart2, label: "Financial Reports" },
  { Icon: Target, label: "Lead Management" },
  { Icon: RefreshCw, label: "Tally Migration" },
  { Icon: Package, label: "Inventory" },
  { Icon: Factory, label: "Manufacturing" },
];

const sectionPadding = "px-5 sm:px-6 md:px-10 lg:px-12";

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = ARTICLES.filter((a) => {
    const matchCat = activeCategory === "All" || a.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.tag.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = filtered.filter((a) => a.featured);
  const rest = filtered.filter((a) => !a.featured);

  return (
    <div className="overflow-x-hidden w-full min-h-screen bg-white">
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-neutral-200/40 to-neutral-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div
          className={`relative z-10 max-w-[1160px] mx-auto pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-16 lg:pb-16 ${sectionPadding}`}
        >
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="max-w-2xl mx-auto md:mx-0 w-full text-center md:text-left flex flex-col items-center md:items-start min-w-0"
          >
            <motion.div variants={fadeUp} className="mb-5 sm:mb-6">
              <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-3.5 sm:px-4 py-1.5 text-[12px] sm:text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#171717] inline-block animate-pulse"></span>
                ABSS Blog
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-[26px] sm:text-[48px] md:text-[56px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-4 sm:mb-6 sentient-bold tracking-tight px-1"
            >
              Insights for Indian SMEs Growing Smarter
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-[15px] sm:text-lg md:text-[21px] font-medium leading-relaxed text-[#262626] mb-7 sm:mb-8 geist-medium max-w-xl px-1"
            >
              Practical guides on GST compliance, accounting, CRM, and building
              efficient operations - written for Indian business owners, not
              accountants.
            </motion.p>

            {/* Search */}
            <motion.div
              variants={fadeUp}
              className="relative w-full max-w-md mx-auto md:mx-0"
            >
              <input
                type="text"
                placeholder="Search articles - GST, Tally migration, E-Invoice…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 sm:h-12 pl-4 pr-12 border border-[#E5E5E5] rounded-lg bg-white geist-medium text-sm text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] transition-colors shadow-sm"
              />
              <svg
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a3a3a3]"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TOPIC PILLS ──────────────────────────────────────────── */}
      <section className="border-b border-[#e5e5e5] bg-white overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-3.5 sm:py-4 min-w-0`}
        >
          <motion.div
            className="flex flex-wrap gap-2 items-center justify-center md:justify-start"
            variants={fastStagger}
            {...viewportOnce}
          >
            <span className="geist-medium text-xs text-[#737373] mr-0 sm:mr-2 w-full sm:w-auto text-center sm:text-left mb-1 sm:mb-0">
              Popular topics:
            </span>
            {FEATURED_TOPICS.map((t, i) => (
              <motion.button
                key={i}
                variants={cardItem}
                onClick={() => setSearchQuery(t.label.split(" ")[0])}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 border border-[#E5E5E5] bg-[#FAFAFA] rounded-lg geist-medium text-[11px] sm:text-xs text-[#525252] hover:border-[#171717] hover:text-[#171717] transition-colors"
              >
                <t.Icon className="w-3 h-3" />
                <span>{t.label}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── ARTICLES ─────────────────────────────────────────────── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          {/* Category Filter */}
          <motion.div
            className="flex flex-wrap gap-2 mb-8 sm:mb-14 justify-center md:justify-start"
            variants={stagger}
            {...viewportOnce}
          >
            {CATEGORIES.map((cat) => (
              <motion.button
                key={cat}
                variants={fadeUp}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[12px] sm:text-sm geist-semibold border rounded-lg transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-[#171717] text-white border-[#171717]"
                    : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#A3A3A3]"
                }`}
              >
                {cat}
              </motion.button>
            ))}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-[12px] sm:text-sm geist-semibold border border-[#171717] text-[#171717] bg-[#F5F5F5] rounded-lg transition-all"
              >
                Clear search ✕
              </button>
            )}
          </motion.div>

          {filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="geist-semibold text-lg text-[#171717] mb-2">
                No articles found
              </p>
              <p className="geist-medium text-sm text-[#525252]">
                Try a different search term or category
              </p>
            </motion.div>
          ) : (
            <>
              {/* Featured articles - 2 col */}
              {featured.length > 0 && (
                <motion.div
                  className="grid md:grid-cols-2 gap-4 sm:gap-8 mb-5 sm:mb-8 min-w-0"
                  variants={stagger}
                  {...viewportOnce}
                >
                  {featured.map((article) => (
                    <motion.div
                      key={article.id}
                      variants={cardItem}
                      className="min-w-0"
                    >
                      <Link
                        href={`/blog-details/${article.slug}`}
                        className="block bg-white border border-[#E5E5E5] rounded-xl sm:rounded-3xl overflow-hidden group hover:border-neutral-300/80 hover:shadow-xl transition-all duration-500 shadow-md sm:shadow-lg h-full min-w-0"
                      >
                        <div className="overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-44 sm:h-52 object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 sm:p-7">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <span className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-full px-2.5 py-1 text-[10px] sm:text-[11px] geist-semibold bg-neutral-50/40 text-neutral-800 shadow-sm">
                              {article.category}
                            </span>
                            <span className="geist-medium text-[11px] sm:text-xs text-[#737373]">
                              {article.tag}
                            </span>
                          </div>
                          <h2 className="text-[18px] sm:text-2xl text-[#171717] leading-[1.3] sm:leading-[1.25] mb-2.5 sm:mb-3 sentient-medium tracking-tight group-hover:text-black transition-colors">
                            {article.title}
                          </h2>
                          <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium mb-4 sm:mb-5">
                            {article.excerpt}
                          </p>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <span className="geist-medium text-[11px] sm:text-xs text-[#737373]">
                              {article.date} · {article.readTime}
                            </span>
                            <span className="geist-semibold text-sm text-[#171717]">
                              Read →
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {/* Rest - 3 col grid */}
              {rest.length > 0 && (
                <motion.div
                  className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 min-w-0"
                  variants={stagger}
                  {...viewportOnce}
                >
                  {rest.map((article) => (
                    <motion.div
                      key={article.id}
                      variants={cardItem}
                      className="min-w-0"
                    >
                      <Link
                        href={`/blog-details/${article.slug}`}
                        className="flex flex-col bg-white border border-[#E5E5E5]/80 rounded-xl sm:rounded-2xl overflow-hidden group hover:border-neutral-300/80 hover:shadow-lg transition-all duration-300 shadow-sm h-full min-w-0"
                      >
                        <div className="overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-40 sm:h-44 object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 sm:p-6 flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                            <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] geist-semibold bg-[#FAFAFA] text-[#525252] shadow-sm">
                              {article.category}
                            </span>
                          </div>
                          <h3 className="text-[15px] sm:text-base font-semibold text-[#171717] geist-semibold tracking-tight leading-relaxed mb-2.5 sm:mb-3 flex-1 group-hover:text-black transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-[#333333] text-[13px] sm:text-[14px] leading-relaxed geist-medium mb-3 sm:mb-4 line-clamp-3">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between mt-auto pt-3 sm:pt-4 border-t border-[#e5e5e5] gap-2">
                            <span className="geist-medium text-[11px] sm:text-xs text-[#737373] min-w-0 truncate">
                              {article.date} · {article.readTime}
                            </span>
                            <span className="geist-semibold text-xs text-[#171717] shrink-0">
                              Read →
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

    </div>
  );
}
